import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { Stack, Text } from '@atlaskit/primitives/compiled';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { getRenderMode } from '../../../common/convertActionStatusToRenderMode';
import { emptyBlastRadiusSummaryMessage } from '../../../common/emptyBlastRadiusSummaryMessage';
import type { ConvertedPlan } from '../../../common/types';
import { IntentBody } from '../../../common/ui/intent-body/IntentBody';
import { useActionPlanApplyState } from '../../../controllers/use-action-plan-apply-state';

import { ActionCardFooter } from './ActionCardFooter';
import { ActionCardWrapper } from './ActionCardWrapper';
import { SubPlan } from './SubPlan';

type Props = {
	convertedPlan: ConvertedPlan;
} & ActionRendererFor<typeof ActionKeys.JiraAdminAgent>;

export const JiraAdminAgentActionPlanCard = ({
	actions,
	conversationId,
	recipientAgentId,
	sendMessageConfirmedTools,
	getStatus,
	messageId,
	isCanvasOpen,
	isCanvasLoading = false,
	openCanvas,
	onDismissed,
	convertedPlan,
}: Props & {
	isCanvasOpen: boolean;
	isCanvasLoading?: boolean;
	openCanvas: () => void;
	onDismissed?: () => void;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	// Drives the footer to the dismissed (red pill) state immediately on dismiss, keeping the plan
	// details visible above it.
	const [isDismissed, setIsDismissed] = useState(false);
	// Dismiss is terminal: once the user dismisses the plan, no further confirmations may be sent,
	// even if an accept was triggered in the same tick. A ref (not state) so the guard is effective
	// synchronously within the current render.
	const isDismissedRef = useRef(false);
	// Only step actions are applyable; the plan action is a container.
	const stepActions = useMemo(
		() => actions.filter((action) => action.data.actionType === 'step'),
		[actions],
	);
	const invocationIds = useMemo(
		() => stepActions.map((action) => action.invocationId),
		[stepActions],
	);
	// `useActionPlanApplyState` requires a `conversationId` string; `conversationId` here is
	// optional (per `AgentActionPayload`), so fall back to `''` the same way
	// `useActionInvocationStatuses` does internally.
	const { perStep, runApply } = useActionPlanApplyState(conversationId ?? '', invocationIds);

	const renderMode = useMemo(
		() =>
			getRenderMode(
				stepActions.map((action) => ({
					storedStatus: action.status || 'planned',
					currentStatus: getStatus(action),
					inProgress: perStep[action.invocationId]?.inProgress ?? false,
					error: perStep[action.invocationId]?.error,
				})),
			),
		[stepActions, getStatus, perStep],
	);

	const onAcceptAll = useCallback(async () => {
		// Do not execute accepted steps once the plan has been dismissed.
		if (isDismissedRef.current) {
			return;
		}

		const applyableActions = stepActions
			.filter((action) => (action.status ?? 'planned') === 'planned')
			.filter((action) => getStatus(action) === 'idle');

		const idsToApply = applyableActions.map((action) => action.invocationId);

		await runApply(idsToApply, async () => {
			await sendMessageConfirmedTools({
				agentId: recipientAgentId,
				confirmed_tools: {
					message_id: messageId,
					tools: applyableActions.map((action) => ({
						key: action.key,
						tool_id: action.actionId,
						tool_source: action.toolSource,
						invocation_id: action.invocationId,
						resolution: {
							type: 'confirmed',
						},
						data: {
							human_message_id: action.humanMessageId ?? '',
							arguments: action.data,
						},
					})),
				},
				conversationId,
			});
		});
	}, [
		stepActions,
		conversationId,
		getStatus,
		messageId,
		recipientAgentId,
		sendMessageConfirmedTools,
		runApply,
	]);

	// NOTE: unlike `onAcceptAll`, this is intentionally left without any inProgress/error tracking,
	// matching its pre-existing behaviour of having no error handling of its own. See the
	// SoftwareEngineer's report for why this wasn't wired into `runApply`.
	const onDismissAll = useCallback(async () => {
		// Idempotent + terminal: guard against a double dismiss or an accept racing the dismiss.
		if (isDismissedRef.current) {
			return;
		}
		isDismissedRef.current = true;
		// Show the dismissed (red pill) footer immediately (optimistic UI) and close the canvas,
		// without waiting for the backend round-trip.
		setIsDismissed(true);
		onDismissed?.();

		const applyableActions = stepActions
			.filter((action) => (action.status ?? 'planned') === 'planned')
			.filter((action) => getStatus(action) === 'idle');

		const idsToApply = applyableActions.map((action) => action.invocationId);

		await runApply(idsToApply, async () => {
			await sendMessageConfirmedTools({
				agentId: recipientAgentId,
				confirmed_tools: {
					message_id: messageId,
					tools: applyableActions.map((action) => ({
						key: action.key,
						tool_id: action.actionId,
						tool_source: action.toolSource,
						invocation_id: action.invocationId,
						resolution: {
							type: 'cancelled',
						},
						data: {
							human_message_id: action.humanMessageId ?? '',
							arguments: action.data,
						},
					})),
				},
				conversationId,
			});
		});
	}, [
		stepActions,
		conversationId,
		getStatus,
		messageId,
		onDismissed,
		runApply,
		recipientAgentId,
		sendMessageConfirmedTools,
	]);

	// A local dismiss shows the cancelled (red pill) footer immediately; otherwise fall back to the
	// backend-derived render mode (also CANCELLED once every step is reported cancelled).
	const effectiveRenderMode = isDismissed ? 'CANCELLED' : renderMode;
	const topLevelPlan = convertedPlan.topLevelPlan;

	if (!topLevelPlan) {
		return <></>;
	}

	const { title, subtext, blastRadius } = topLevelPlan.payload;

	return (
		<ActionCardWrapper title={title} subtitle={subtext} defaultExpanded isExpandable>
			<Stack space="space.150">
				<Stack>
					{topLevelPlan.plans.map((subPlan, index) => (
						<SubPlan
							key={subPlan.id}
							plan={subPlan}
							isFirst={index === 0}
							isLast={index === topLevelPlan.plans.length - 1}
							number={index + 1}
						/>
					))}
					{/* A step parented directly to the plan (e.g. a single-step ask) renders inline,
					    as there is no sub-plan to wrap it - this scenario has no sub-plans or additional steps */}
					{<IntentBody steps={topLevelPlan.steps} />}
				</Stack>
				{blastRadius?.summary === undefined ? (
					<Text color="color.text.subtlest">{formatMessage(emptyBlastRadiusSummaryMessage)}</Text>
				) : (
					<Text color="color.text.subtlest">{blastRadius.summary}</Text>
				)}
			</Stack>
			<ActionCardFooter
				renderMode={effectiveRenderMode}
				isCanvasOpen={isCanvasOpen}
				isCanvasLoading={isCanvasLoading}
				onAcceptPlan={onAcceptAll}
				onDismissPlan={onDismissAll}
				onReviewPlan={openCanvas}
			/>
		</ActionCardWrapper>
	);
};
